import { ThemeName } from "@ngt/forms/src";

export interface ISentrySettings {
    dsn?: string;
    customer: string;
    environment: string;
    sendDefaultPii: boolean;
    enableMetrics: boolean;
    attachStacktrace: boolean;
    debug: boolean;
    application: string;
    release?: string;
    replaysSessionSampleRate: number;
    replaysOnErrorSampleRate: number;
    enableLogs: boolean;
    tracesSampleRate: number;
    enableReportABug: boolean;
}

export interface IPaletteSettings {
    primaryLight: string;
    secondaryLight: string;
    primaryDark: string;
    secondaryDark: string;
    breadcrumbBgLight: string;
    breadcrumbBgDark: string;
    appBarBgDark: string;
    backgroundAltDark: string;
}

export interface IAppSettings {
    trialPortalUrl: string;
    sentry: ISentrySettings;
    overrideTheme?: ThemeName;
    palette: IPaletteSettings;
}

type SentrySettings = ISentrySettings;

interface GetClientAppSettingsResponse {
    settings?: {
        sentry?: Partial<ISentrySettings>;
    };
}

const ClientAppSettingsResponse: GetClientAppSettingsResponse | undefined = (window as any).APP_SETTINGS;

const getSentryAppSettingValue = <TData>(
    appSettingProperty: keyof SentrySettings | undefined
): TData | undefined => {
    if (!appSettingProperty) {
        return undefined;
    }

    const value = ClientAppSettingsResponse?.settings?.sentry?.[appSettingProperty] as TData | undefined;

    if (value === undefined || value === null) {
        return undefined;
    }

    return value;
};

const getSentryEnvironmentValue = (
    environmentProperty: string | undefined
): string | undefined => {
    if (!environmentProperty) {
        return undefined;
    }

    const value: string | undefined = import.meta.env[environmentProperty];

    if (value === undefined || value === null || value === "") {
        return undefined;
    }

    return value;
};

const getSentryEnvironmentString = (
    environmentProperty: string | undefined
): string | undefined => getSentryEnvironmentValue(environmentProperty);

const getSentryEnvironmentNumber = (
    environmentProperty: string | undefined
): number | undefined => {
    const valueStr = getSentryEnvironmentValue(environmentProperty);

    if (!valueStr) {
        return undefined;
    }

    const parsed = Number(valueStr);
    return Number.isNaN(parsed) ? undefined : parsed;
};

const getSentryEnvironmentBoolean = (
    environmentProperty: string | undefined
): boolean | undefined => {
    const valueStr = getSentryEnvironmentValue(environmentProperty);

    if (!valueStr) {
        return undefined;
    }

    const lowerCase = valueStr.toLowerCase();

    if (lowerCase === "1" || lowerCase === "true") {
        return true;
    }

    if (lowerCase === "0" || lowerCase === "false") {
        return false;
    }

    return undefined;
};

const getSentryString = (
    appSettingProperty: keyof SentrySettings | undefined,
    environmentProperty: string | undefined
) => {
    const appSettingValue = getSentryAppSettingValue<string>(appSettingProperty);

    if (appSettingValue) {
        return appSettingValue;
    }

    const environmentValue = getSentryEnvironmentString(environmentProperty);

    if (environmentValue) {
        return environmentValue;
    }

    return undefined;
};

const getSentryNumber = (
    appSettingProperty: keyof SentrySettings | undefined,
    environmentProperty: string | undefined
) => {
    const appSettingValue = getSentryAppSettingValue<number>(appSettingProperty);

    if (appSettingValue !== null && appSettingValue !== undefined) {
        return appSettingValue;
    }

    const environmentValue = getSentryEnvironmentNumber(environmentProperty);

    if (environmentValue !== null && environmentValue !== undefined) {
        return environmentValue;
    }

    return undefined;
};

const getSentryBoolean = (
    appSettingProperty: keyof SentrySettings | undefined,
    environmentProperty: string | undefined
) => {
    const appSettingValue = getSentryAppSettingValue<boolean>(appSettingProperty);

    if (appSettingValue !== null && appSettingValue !== undefined) {
        return appSettingValue;
    }

    const environmentValue = getSentryEnvironmentBoolean(environmentProperty);

    if (environmentValue !== null && environmentValue !== undefined) {
        return environmentValue;
    }

    return undefined;
};

const AppSettings: IAppSettings = {
    trialPortalUrl: (window as any).TRIAL_PORTAL_URL ?? import.meta.env.VITE_TRIALPORTAL_BASE_URL,
    sentry: {
        dsn: getSentryString("dsn", "VITE_SENTRY_DSN"),
        debug: getSentryBoolean("debug", "VITE_SENTRY_DEBUG") ?? false,
        enableMetrics: getSentryBoolean("enableMetrics", "VITE_SENTRY_ENABLE_METRICS") ?? false,
        sendDefaultPii: getSentryBoolean("sendDefaultPii", "VITE_SENTRY_SEND_DEFAULT_PII") ?? false,
        attachStacktrace: getSentryBoolean("attachStacktrace", "VITE_SENTRY_ATTACH_STACKTRACE") ?? false,
        customer: getSentryString("customer", "VITE_SENTRY_CUSTOMER") ?? "Unknown",
        environment: getSentryString("environment", "VITE_SENTRY_ENVIRONMENT") ?? "Development",
        application: getSentryString(undefined, "VITE_SENTRY_APPLICATION") ?? "SourceDataVerification",
        release: getSentryString(undefined, "VITE_SENTRY_RELEASE"),
        enableLogs: getSentryBoolean("enableLogs", "VITE_SENTRY_ENABLE_LOGS") ?? false,
        replaysOnErrorSampleRate: getSentryNumber("replaysOnErrorSampleRate", "VITE_SENTRY_REPLAY_ON_ERROR_SAMPLE_RATE") ?? 1.0,
        replaysSessionSampleRate: getSentryNumber("replaysSessionSampleRate", "VITE_SENTRY_REPLAY_SESSION_SAMPLE_RATE") ?? 0.1,
        tracesSampleRate: getSentryNumber("tracesSampleRate", "VITE_SENTRY_TRACE_SAMPLE_RATE") ?? 0.2,
        enableReportABug: getSentryBoolean("enableReportABug", "VITE_SENTRY_ENABLE_REPORT_A_BUG") ?? false
    },
    palette: {
        primaryDark: "#7d6699",
        primaryLight: "#562c85",
        secondaryDark: "#99567e",
        secondaryLight: "#e8078c",
        breadcrumbBgLight: "#dce0e3",
        breadcrumbBgDark: "#262626",
        appBarBgDark: "#272727",
        backgroundAltDark: "#262626"
    }
};

export default AppSettings;
